/** React imports */
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
/** App imports */
import { DrillholeContext } from '../Contexts/DrillholeContext';
import { appContext } from '../Contexts/AppContext';
import {
  deleteLog,
  deleteLogDataItems,
  getAllBulkLogDataItems,
  getLog,
  getLogDataItems,
  getLogs,
  updateLog,
} from '../utils/authenticated-requests/logs';
import { isEmpty } from 'lodash';
import { Dataframe } from '../TypeScript/types';
import { LogsApiDataItem } from '@goldspotdiscoveries/types';

export const LOG_DATA_LIMIT_DEFAULT = '10';
export const LOG_DATA_PREVIEW_LIMIT = '20';

export const LOG_TYPE_LIST_QUERY_KEY = 'logTypeList';
export const LOG_LIST_QUERY_KEY = 'logList';
export const LOG_SINGLE_QUERY_KEY = 'log';
export const LOG_DATA_QUERY_KEY = 'logData';
export const LOG_BULK_DATA_QUERY_KEY = 'logBulkData';

/**
 * Hook to invalidate the log single query.
 */
export const useInvalidateLogSingleQuery = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [LOG_SINGLE_QUERY_KEY],
    });
};

/**
 * Extacts a list of log types from a JSON string error.
 * Ignores "lithology".
 *
 * @param jsonStringError string The string to parse for log types
 * @example
 * `{"status_code": 400, "message": "The query is not valid", "error": "'litholens.*' is not one of ['custom', 'lithology', 'someOtherType' ]"}`
 * @returns string[] The log type list
 * @example
 * ['custom', 'someOtherType']
 */
export const extractLogTypes = (jsonStringError: string): string[] => {
  if (!jsonStringError) {
    return [];
  }
  // Extract the logTypes from error.response.data.error
  try {
    // TODO: Holdover for old API response. Remove as soon as new API deployed

    return JSON.parse(jsonStringError)
      .error.match(/\[(.*?)]/)[1]
      .replace(/'/g, '')
      .split(', ');
  } catch (e) {
    const substring = /\[.+]/.exec(jsonStringError)?.at(0);

    if (substring == null || isEmpty(substring)) {
      return [];
    }

    const array = JSON.parse(substring.replaceAll("\'", '\"'));

    return array as string[];
  }
};

/**
 * Fetches the list of log types for a given company and project.
 * Can be use to get a list of log types from the error response.
 *
 * @param company string The name of the company.
 * @param project string The ID of the project.
 * @returns The result of the query containing the log types.
 *
 * @example
 * const { data, error, isLoading } = useGetLogTypeList('CompanyA', 'Project123');
 * const types = extractLogTypes(error?.error?.response?.data?.error);
 */
export const useGetLogTypeList = (company: string, project: string) => {
  return useQuery({
    queryKey: [LOG_TYPE_LIST_QUERY_KEY, company, project],
    queryFn: () =>
      getLogs({
        company,
        projectId: project,
        queryParams: { limit: 1, type: 'litholens.*' },
      }),
    enabled: !!company && !!project,
  });
};

/**
 * Fetches a single log by its ID for a given company and project.
 * Is enabled when all parameters are truthy.
 *
 * @param company string The name of the company.
 * @param projectId string The ID of the project.
 * @param logId string The ID of the log to fetch.
 * @returns The result of the query containing the log data.
 *
 * @example
 * const { data, error, isLoading } = useGetLog('CompanyA', 'Project123', 'LogId');
 */
export const useGetLog = (
  company: string,
  projectId: string,
  logId: string,
) => {
  return useQuery({
    queryKey: [LOG_SINGLE_QUERY_KEY, company, projectId, logId],
    queryFn: () =>
      getLog({
        company,
        projectId,
        logId,
      }),
    enabled: !!company && !!projectId && !!logId,
  });
};

/**
 * Updates a log entry for a given company and project.
 *
 * @param company string The name of the company.
 * @param projectId string The ID of the project.
 * @returns A mutation object from `useMutation` to handle log updates.
 */
export const useUpdateLog = (company: string, projectId: string) => {
  return useMutation({
    mutationFn: ({
      logId,
      body,
    }: {
      logId: string;
      body: Record<string, string | number | boolean>;
    }) => updateLog(company, projectId, logId, body),
  });
};

/**
 * Fetches log queries for a given company and project.
 *
 * @param company string The name of the company.
 * @param projectId string The ID of the project.
 * @param selectedLogs string[] An array of selected logs, each containing a logId.
 * @returns An array of query results for the selected logs.
 *
 * @example
 * const logQueries = useGetLogQueries('CompanyA', 'Project123', [{ logId: 'log1' }, { logId: 'log2' }]);
 */
export const useGetLogQueries = (
  company: string,
  projectId: string,
  selectedLogs: { logId: string }[],
) => {
  return useQueries({
    queries: selectedLogs.map(({ logId }) => ({
      queryKey: [LOG_SINGLE_QUERY_KEY, company, projectId, logId],
      enabled: !!company && !!projectId,
      queryFn: () =>
        getLog({
          company,
          projectId,
          logId,
        }),
    })),
  });
};

/**
 * Fetches an infinite list of logs based on the provided company, project ID, and log type filter.
 *
 * @param company string The name of the company.
 * @param projectId string The ID of the project.
 * @param logTypeFilter string The type of logs to filter by.
 * @returns An object containing the infinite query result for the log list.
 *
 * @example
 * const { data, isLoading, refetch } = useGetLogListInfinite('companyA', 'project123', 'custom');
 */
export const useGetLogListInfinite = (
  company: string,
  projectId: string,
  logTypeFilter?: string,
) => {
  const { t } = useTranslation();
  const params: Record<string, string | number | null> = {
    limit: 100,
  };
  if (logTypeFilter) {
    params['type'] = logTypeFilter;
  }
  return useInfiniteQuery({
    queryKey: [LOG_LIST_QUERY_KEY, company, projectId, logTypeFilter],
    queryFn: async ({ pageParam }) =>
      getLogs({
        company,
        projectId,
        queryParams: {
          ...params,
          offset_token: pageParam.length === 0 ? undefined : pageParam,
        },
      }),
    initialPageParam: '',
    getNextPageParam: ({ next_offset_token }) => next_offset_token,
    enabled: !!company && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 Minutes in ms
    meta: {
      errorMessage: t('ERROR_FETCHING_LOGS'),
    },
  });
};

/**
 * Deletes the log items for a given company, project, and log id.
 *
 * @param company string The name of the company.
 * @param projectId string The ID of the project.
 * @returns A mutation object to delete log items.
 *
 * @example
 * const { mutate } = useDeleteLogItems('companyA', 'project123');
 * mutate('LogId');
 *
 * @remarks
 * This hook uses the `useMutation` hook from React Query to handle the deletion of log items.
 * If user is admin: deletes items by log id
 * If user is non-admin:
 * It retrieves the drillhole IDs and names from the `DrillholeContext` and constructs a `drillhole_id` string
 * that is used in the deletion request.
 */
export const useDeleteLogItems = (company: string, projectId: string) => {
  const { user } = useContext(appContext);
  const { drillholesMap } = useContext(DrillholeContext);
  const queryParams: Record<string, string> = {};
  if (user.signInUserSession.idToken.payload['custom:admin'] !== 'true') {
    // non-admin users can only delete log items by drillhole
    const drillholeIdsAndNames = Object.keys(drillholesMap.byId).concat(
      Object.keys(drillholesMap.byName),
    );
    const drillhole_id: string = `[${drillholeIdsAndNames.join(',')}]`;
    queryParams['drillhole_id'] = drillhole_id;
  }
  return useMutation({
    mutationFn: (logId: string) => {
      return deleteLogDataItems(company, projectId, logId, queryParams);
    },
  });
};

/**
 * Deletes a log for a given company, project, and log id.
 *
 * This hook uses a mutation to delete a log entry for a given company and project.
 *
 * @param company string The name of the company.
 * @param projectId string The ID of the project.
 * @returns A mutation object that can be used to trigger the deletion of a log entry.
 */
export const useDeleteLog = (company: string, projectId: string) => {
  return useMutation({
    mutationFn: (logId: string) => {
      return deleteLog(company, projectId, logId);
    },
  });
};

export const useGetLogDataItems = (
  company: string,
  projectId: string,
  logId: string,
  queryParams?: Record<string, string>,
) => {
  return useQuery({
    queryKey: [LOG_DATA_QUERY_KEY, company, projectId, logId],
    queryFn: () =>
      getLogDataItems<{
        items: LogsApiDataItem[];
      }>({
        company,
        projectId,
        logId,
        queryParams: {
          limit: LOG_DATA_LIMIT_DEFAULT,
          ...queryParams,
        },
      }),
    enabled: !!company && !!projectId && !!logId,
  });
};

/**
 * Fetches all log data items for a given log using the bulk endpoint.
 *
 * Note that the endpoint returns a dataframe with arrays rather than number-indexed objects, but the final output is cast to Dataframe for compatibility.
 */
export const useGetLogDataItemsBulk = (
  company: string,
  projectId: string,
  logId: string,
  queryParams?: Record<string, string>,
) => {
  const effectiveQueryParams = {
    ...queryParams,
    bulk: 'true' as const,
  };
  const { data, isPending, isFetching, isError, error } = useQuery({
    queryKey: [LOG_BULK_DATA_QUERY_KEY, company, projectId, logId],
    queryFn: () =>
      getAllBulkLogDataItems({
        company,
        projectId,
        logId,
        queryParams: effectiveQueryParams,
      }),
    enabled: !!company && !!projectId && !!logId,
  });

  return {
    data: data as unknown as Dataframe,
    isLoading: isPending || isFetching,
    isError,
    error,
  };
};
